import { create } from "zustand";
import { persist } from "zustand/middleware";
import { PLAYABLE_TRACKS } from "@/content/music";

type MusicStatus = "off" | "playing" | "paused";

type MusicState = {
  status: MusicStatus;
  /** Index into PLAYABLE_TRACKS — ignored when status is "off". */
  trackIndex: number;
  /** 0..1 */
  volume: number;
  /** Whether the shelf panel is expanded. Not persisted. */
  expanded: boolean;
  enable: () => void;
  disable: () => void;
  toggle: () => void;
  setExpanded: (open: boolean) => void;
  setVolume: (v: number) => void;
  next: () => void;
  prev: () => void;
  jumpTo: (index: number) => void;
};

export const useMusicStore = create<MusicState>()(
  persist(
    (set) => ({
      // Default to "playing" on first visit — music drifts in quietly to help
      // children focus. Returning visitors keep whatever they last set.
      status: "playing",
      trackIndex: 0,
      // Quiet by design. The volume slider goes 0..1 and is persisted.
      volume: 0.18,
      expanded: false,
      enable: () => set({ status: "playing" }),
      disable: () => set({ status: "off" }),
      toggle: () =>
        set((s) => ({
          status: s.status === "playing" ? "off" : "playing",
        })),
      setExpanded: (open) => set({ expanded: open }),
      setVolume: (v) => set({ volume: Math.max(0, Math.min(1, v)) }),
      next: () =>
        set((s) => ({
          trackIndex:
            PLAYABLE_TRACKS.length > 0
              ? (s.trackIndex + 1) % PLAYABLE_TRACKS.length
              : 0,
        })),
      prev: () =>
        set((s) => ({
          trackIndex:
            PLAYABLE_TRACKS.length > 0
              ? (s.trackIndex - 1 + PLAYABLE_TRACKS.length) % PLAYABLE_TRACKS.length
              : 0,
        })),
      jumpTo: (index) =>
        set({
          trackIndex: Math.max(0, Math.min(PLAYABLE_TRACKS.length - 1, index)),
          status: "playing",
        }),
    }),
    {
      name: "schola-games:music",
      partialize: (s) => ({
        status: s.status,
        volume: s.volume,
        trackIndex: s.trackIndex,
      }),
    },
  ),
);
